import { Vector3 } from 'three';

import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { LengthUnit } from 'app/modules/wallforming/components/scenes/types/lengthUnit';

import type { SceneSliceState } from 'app/modules/wallforming/components/state-management/placement/states/sceneSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

export const createSceneSlice: StateCreator<PlacementState, [], [], SceneSliceState> = (set, get) => ({
    pointerPositionRef: { current: null },
    setPointerPositionRef: (position) => {
        const state = get();
        if (!position) {
            state.pointerPositionRef.current = null;
            return;
        }

        if (!state.pointerPositionRef.current) {
            state.pointerPositionRef.current = new Vector3(position.x, position.y, position.z);
        } else {
            state.pointerPositionRef.current.set(position.x, position.y, position.z);
        }
    },

    mainWebGLRendererRef: { current: null },
    setMainWebGLRendererRef: (renderer) => {
        const state = get();
        if (state.mainWebGLRendererRef.current !== renderer) {
            state.mainWebGLRendererRef.current = renderer;
        }
    },

    mainRaycasterRef: { current: null },
    setMainRaycasterRef: (raycaster) => {
        const state = get();
        if (state.mainRaycasterRef.current !== raycaster) {
            state.mainRaycasterRef.current = raycaster;
        }
    },

    isDragNewArticleStartRef: { current: false },
    setIsDragNewArticleStartRef: (dragStarted) => {
        const state = get();
        if (state.isDragNewArticleStartRef.current !== dragStarted) {
            state.isDragNewArticleStartRef.current = dragStarted;
        }
    },

    isDragActiveRef: { current: false },
    setIsDragActiveRef: (isActive) => {
        const state = get();
        if (state.isDragActiveRef.current !== isActive) {
            state.isDragActiveRef.current = isActive;
        }
    },

    isDragNewArticleActiveRef: { current: false },
    setIsDragNewArticleActiveRef: (isActive) => {
        const state = get();
        if (state.isDragNewArticleActiveRef.current !== isActive) {
            state.isDragNewArticleActiveRef.current = isActive;
        }
    },

    isAutoDragActiveRef: { current: false },
    setIsAutoDragActiveRef: (isActive) => {
        const state = get();
        if (state.isAutoDragActiveRef.current !== isActive) {
            state.isAutoDragActiveRef.current = isActive;
        }

        if (isActive) {
            // Disable navigation to other POI types during auto drag
            const hiddenTypes = Object.values(POIFilterType).filter(
                (filterType) => filterType !== state.poiSelection.current.filterType
            );
            set({ hiddenPOIFilterTypes: hiddenTypes });
        }
    },

    isZAxisDragActiveRef: { current: false },
    setIsZAxisDragActiveRef: (isActive) => {
        const state = get();
        if (state.isZAxisDragActiveRef.current !== isActive) {
            state.isZAxisDragActiveRef.current = isActive;
        }
    },

    isTransformationActiveRef: { current: false },
    setIsTransformationActiveRef: (isActive) => {
        const state = get();
        if (state.isTransformationActiveRef.current !== isActive) {
            state.isTransformationActiveRef.current = isActive;
        }
    },

    isDraggingActive: () => {
        const state = get();
        return (
            (state.isDragActiveRef.current && !state.isAutoDragActiveRef.current) ||
            state.isDragNewArticleActiveRef.current ||
            state.isTransformationActiveRef.current
        );
    },

    pivotControlsRef: { current: null },
    setPivotControlsRef: (pivotControls) => {
        const state = get();
        if (state.pivotControlsRef.current !== pivotControls) {
            state.pivotControlsRef.current = pivotControls;
        }
    },

    arePivotControlsActive: false,
    setArePivotControlsActive: (areActive) => set({ arePivotControlsActive: areActive }),

    selectionBox: null,
    setSelectionBox: (selectionBox) => set({ selectionBox }),

    insertionPoint: new Vector3(0, 0, 0),
    setInsertionPoint: (x, y, z) => {
        const state = get();
        state.insertionPoint.set(x, y, z);
    },

    wallBottomZ: 0,
    setWallBottomZ: (z: number) => set({ wallBottomZ: z }),

    clipboard: [],

    isZPositionUpdatePending: false,
    setIsZPositionUpdatePending: (isPending: boolean) => set({ isZPositionUpdatePending: isPending }),

    lengthUnit: LengthUnit.CENTIMETER,
    setLengthUnit: (lengthUnit: LengthUnit) => set({ lengthUnit })
});
